/**
 * AuditLog.js
 * Immutable audit repository for compliance and records retention.
 * 7-year retention policy.
 * Categories: Deployment, Approval, Rollback, Security, Admin, System
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', {
    day:'2-digit', month:'short', year:'numeric',
    hour:'2-digit', minute:'2-digit', second:'2-digit'
  });
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
}
function timeAgo(d) {
  if (!d) return '—';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const day = Math.floor(diff / 86400000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${day}d ago`;
}

const CAT_STYLE = {
  deployment: { bg:'#eff4ff', color:'#1a56db', icon:'📦' },
  approval:   { bg:'#fef3c7', color:'#d97706', icon:'✅' },
  rollback:   { bg:'#ede9fe', color:'#7c3aed', icon:'↩' },
  security:   { bg:'#fee2e2', color:'#dc2626', icon:'🔐' },
  admin:      { bg:'#f0fdf4', color:'#16a34a', icon:'👤' },
  system:     { bg:'#f1f5f9', color:'#64748b', icon:'⚙' },
};

const SEV_STYLE = {
  info:     { bg:'#f0fdf4', color:'#16a34a' },
  warning:  { bg:'#fef3c7', color:'#d97706' },
  critical: { bg:'#fee2e2', color:'#dc2626' },
};

const RESULT_STYLE = {
  success: { bg:'#dcfce7', color:'#16a34a', label:'✔ Success' },
  failure: { bg:'#fee2e2', color:'#dc2626', label:'✘ Failed' },
  pending: { bg:'#fef3c7', color:'#d97706', label:'⏳ Pending' },
};

export default function AuditLog() {
  const [logs,     setLogs]     = useState([]);
  const [summary,  setSummary]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [pages,    setPages]    = useState(1);

  // Filters
  const [category,  setCategory]  = useState('');
  const [result,    setResult]    = useState('');
  const [severity,  setSeverity]  = useState('');
  const [search,    setSearch]    = useState('');
  const [dateFrom,  setDateFrom]  = useState('');
  const [dateTo,    setDateTo]    = useState('');
  const [expandRow, setExpandRow] = useState(null);
  const [msg,       setMsg]       = useState('');

  const loadSummary = useCallback(async () => {
    try {
      const r = await api.getAuditSummary();
      setSummary(r.data);
    } catch (e) { console.error('Audit summary:', e.message); }
  }, []);

  const loadLogs = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 50 };
      if (category) params.category = category;
      if (result)   params.result   = result;
      if (severity) params.severity = severity;
      if (search)   params.search   = search;
      if (dateFrom) params.from     = dateFrom;
      if (dateTo)   params.to       = dateTo + 'T23:59:59';

      const r = await api.getAuditLogs(params);
      setLogs(r.data.logs || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
      setPage(pg);
    } catch (e) {
      setMsg('Failed to load audit logs: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, [category, result, severity, search, dateFrom, dateTo]);

  useEffect(() => { loadSummary(); loadLogs(1); }, [loadSummary, loadLogs]);

  function handleExport() {
    const params = {};
    if (category) params.category = category;
    if (dateFrom) params.from = dateFrom;
    if (dateTo)   params.to   = dateTo;
    const url = api.exportAuditLogs(params);
    window.open(url, '_blank');
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">📋 Audit Repository</div>
          <div className="page-subtitle">
            Immutable records — Deployments · Approvals · Rollbacks · 7-year retention
          </div>
        </div>
        <div className="flex-gap">
          <button className="btn btn-ghost" onClick={() => loadLogs(1)} disabled={loading}>
            ↻ Refresh
          </button>
          <button className="btn btn-outline" onClick={handleExport}>
            ⬇ Export CSV
          </button>
        </div>
      </div>

      {msg && <div className="alert error" style={{ marginBottom:16 }}>{msg}</div>}

      {/* Retention policy banner */}
      <div style={{ background:'#eff4ff', border:'1px solid #bfdbfe', borderRadius:10,
        padding:'12px 20px', marginBottom:20, display:'flex', alignItems:'center', gap:12 }}>
        <span style={{ fontSize:20 }}>🔒</span>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:'#1e2a3b' }}>
            7-Year Records Retention Policy
          </div>
          <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>
            All audit records are immutable and retained for 7 years per compliance requirements.
            {summary?.retentionInfo?.oldestRecord && (
              <> Oldest record: <strong>{fmtDate(summary.retentionInfo.oldestRecord)}</strong>.</>
            )}
            {' '}Records cannot be modified or deleted before retention period expires.
          </div>
        </div>
        {summary && (
          <div style={{ marginLeft:'auto', textAlign:'right' }}>
            <div style={{ fontSize:20, fontWeight:800, color:'#1a56db' }}>{summary.total?.toLocaleString()}</div>
            <div style={{ fontSize:10, color:'#64748b' }}>total records</div>
          </div>
        )}
      </div>

      {/* Category summary cards */}
      {summary?.categoryCounts && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:10, marginBottom:20 }}>
          {['deployment','approval','rollback','security','admin','system'].map(cat => {
            const cs = CAT_STYLE[cat] || CAT_STYLE.system;
            const count = summary.categoryCounts.find(c => c.category === cat)?.count || 0;
            return (
              <div key={cat} onClick={() => { setCategory(cat === category ? '' : cat); loadLogs(1); }}
                style={{ background: category===cat ? cs.color : cs.bg,
                  color: category===cat ? '#fff' : cs.color,
                  border:`1px solid ${cs.bg}`, borderRadius:10, padding:'12px 14px',
                  cursor:'pointer', textAlign:'center', transition:'all 0.15s' }}>
                <div style={{ fontSize:20, marginBottom:4 }}>{cs.icon}</div>
                <div style={{ fontSize:18, fontWeight:800 }}>{parseInt(count).toLocaleString()}</div>
                <div style={{ fontSize:10, fontWeight:600, textTransform:'uppercase',
                  letterSpacing:'0.5px', marginTop:2, opacity:0.8 }}>{cat}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <input className="form-input" style={{ maxWidth:240 }}
          placeholder="Search action, resource, actor..."
          value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && loadLogs(1)} />

        <select className="form-input" style={{ maxWidth:150 }}
          value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}>
          <option value="">All Categories</option>
          {['deployment','approval','rollback','security','admin','system'].map(c => (
            <option key={c} value={c}>{CAT_STYLE[c]?.icon} {c.charAt(0).toUpperCase()+c.slice(1)}</option>
          ))}
        </select>

        <select className="form-input" style={{ maxWidth:130 }}
          value={result} onChange={e => { setResult(e.target.value); setPage(1); }}>
          <option value="">All Results</option>
          <option value="success">✔ Success</option>
          <option value="failure">✘ Failure</option>
          <option value="pending">⏳ Pending</option>
        </select>

        <select className="form-input" style={{ maxWidth:130 }}
          value={severity} onChange={e => { setSeverity(e.target.value); setPage(1); }}>
          <option value="">All Severity</option>
          <option value="info">Info</option>
          <option value="warning">Warning</option>
          <option value="critical">Critical</option>
        </select>

        <input type="date" className="form-input" style={{ maxWidth:140 }}
          value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          title="From date" />
        <input type="date" className="form-input" style={{ maxWidth:140 }}
          value={dateTo} onChange={e => setDateTo(e.target.value)}
          title="To date" />

        <button className="btn btn-primary btn-sm" onClick={() => loadLogs(1)}>
          Apply
        </button>
        {(category || result || severity || search || dateFrom || dateTo) && (
          <button className="btn btn-ghost btn-sm" onClick={() => {
            setCategory(''); setResult(''); setSeverity('');
            setSearch(''); setDateFrom(''); setDateTo('');
            setTimeout(() => loadLogs(1), 0);
          }}>
            ✕ Clear
          </button>
        )}

        <span style={{ marginLeft:'auto', fontSize:12, color:'#94a3b8' }}>
          {total.toLocaleString()} record{total !== 1 ? 's' : ''}
          {pages > 1 && ` · page ${page} of ${pages}`}
        </span>
      </div>

      {/* Audit table */}
      <div className="panel">
        {loading ? <div className="loading">Loading audit records...</div> : (
          <table>
            <thead>
              <tr>
                <th style={{ width:170 }}>Timestamp</th>
                <th style={{ width:100 }}>Category</th>
                <th style={{ width:80 }}>Severity</th>
                <th>Action / Event</th>
                <th style={{ width:130 }}>Actor</th>
                <th style={{ width:160 }}>Resource</th>
                <th style={{ width:90 }}>Result</th>
                <th style={{ width:36 }}></th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && (
                <tr><td colSpan={8} className="empty">
                  {total === 0
                    ? 'No audit records yet. Events will appear here as actions are performed.'
                    : 'No records match your filters.'}
                </td></tr>
              )}
              {logs.map((log, i) => {
                const cs = CAT_STYLE[log.category] || CAT_STYLE.system;
                const rs = RESULT_STYLE[log.result]  || RESULT_STYLE.success;
                const ss = SEV_STYLE[log.severity]   || SEV_STYLE.info;
                const isExpanded = expandRow === log.id;
                return (
                  <React.Fragment key={log.id}>
                    <tr style={{ background: log.result === 'failure' ? '#fff8f8' : undefined }}>
                      <td>
                        <div style={{ fontSize:12, fontWeight:500, color:'#1e2a3b' }}>
                          {fmtDateTime(log.createdAt)}
                        </div>
                        <div style={{ fontSize:10, color:'#94a3b8' }}>{timeAgo(log.createdAt)}</div>
                      </td>
                      <td>
                        <span style={{ fontSize:11, fontWeight:700, background:cs.bg,
                          color:cs.color, padding:'2px 8px', borderRadius:12 }}>
                          {cs.icon} {log.category}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize:10, fontWeight:700, background:ss.bg,
                          color:ss.color, padding:'2px 7px', borderRadius:10 }}>
                          {log.severity}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize:12, fontWeight:500, color:'#1e2a3b', maxWidth:300 }}>
                          {log.action}
                        </div>
                        <div style={{ fontSize:10, color:'#94a3b8', marginTop:1 }}>
                          {log.eventType}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize:12, fontWeight:600, color:'#1e2a3b' }}>
                          {log.actorName || '—'}
                        </div>
                        <div style={{ fontSize:10, color:'#94a3b8' }}>
                          {log.actorRole}{log.actorIp ? ` · ${log.actorIp}` : ''}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize:12, color:'#374151' }}>{log.resourceType || '—'}</div>
                        <div style={{ fontSize:10, color:'#94a3b8', maxWidth:140,
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {log.resourceName || '—'}
                        </div>
                      </td>
                      <td>
                        <span style={{ fontSize:10, fontWeight:700, background:rs.bg,
                          color:rs.color, padding:'2px 8px', borderRadius:12 }}>
                          {rs.label}
                        </span>
                        {log.errorMsg && (
                          <div style={{ fontSize:9, color:'#dc2626', marginTop:2 }}>
                            {log.errorMsg.substring(0,40)}
                          </div>
                        )}
                      </td>
                      <td>
                        <button onClick={() => setExpandRow(isExpanded ? null : log.id)}
                          style={{ background:'none', border:'none', cursor:'pointer',
                            fontSize:12, color:'#94a3b8', padding:'2px 4px' }}>
                          {isExpanded ? '▲' : '▼'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} style={{ background:'#f8fafc', padding:'12px 20px',
                          borderTop:'1px solid #e8ecf4' }}>
                          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
                            <div>
                              <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8',
                                textTransform:'uppercase', marginBottom:4 }}>Record ID</div>
                              <code style={{ fontSize:10, color:'#64748b' }}>{log.id}</code>
                            </div>
                            <div>
                              <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8',
                                textTransform:'uppercase', marginBottom:4 }}>Retain Until</div>
                              <div style={{ fontSize:12, color:'#1e2a3b' }}>{fmtDate(log.retainUntil)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8',
                                textTransform:'uppercase', marginBottom:4 }}>Resource ID</div>
                              <code style={{ fontSize:10, color:'#64748b' }}>{log.resourceId || '—'}</code>
                            </div>
                            {log.details && (
                              <div style={{ gridColumn:'1/-1' }}>
                                <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8',
                                  textTransform:'uppercase', marginBottom:4 }}>Event Details</div>
                                <pre style={{ margin:0, fontSize:11, color:'#374151',
                                  background:'#f1f5f9', padding:'8px 12px', borderRadius:6,
                                  overflow:'auto', maxHeight:120 }}>
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              </div>
                            )}
                            {log.errorMsg && (
                              <div style={{ gridColumn:'1/-1' }}>
                                <div style={{ fontSize:10, fontWeight:700, color:'#dc2626',
                                  textTransform:'uppercase', marginBottom:4 }}>Error</div>
                                <div style={{ fontSize:11, color:'#dc2626', background:'#fee2e2',
                                  padding:'6px 10px', borderRadius:6 }}>{log.errorMsg}</div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ padding:'12px 20px', borderTop:'1px solid #f0f4ff',
            display:'flex', gap:8, alignItems:'center', justifyContent:'center' }}>
            <button className="btn btn-sm btn-outline" disabled={page <= 1}
              onClick={() => loadLogs(page - 1)}>← Prev</button>
            <span style={{ fontSize:12, color:'#94a3b8' }}>
              Page {page} of {pages} · {total.toLocaleString()} records
            </span>
            <button className="btn btn-sm btn-outline" disabled={page >= pages}
              onClick={() => loadLogs(page + 1)}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}
